import CookieHelper from '_utils/CookieHelper';
import EventBus from '_utils/EventBus';
import GaHelper from '_utils/GaHelper';
import StringHelper from '_utils/StringHelper';
import { postJson } from '_core/Api2';

document.addEventListener('DOMContentLoaded', () => {
  signUpPage.wire();
  signUpPage.trackSignupInitiated();
});

const signUpPage = {
  /*
  ==================================================
    WIRE UI ELEMENTS
  ==================================================
  */

  wire() {
    document
      .querySelector('.google-login-button')
      ?.addEventListener('click', () =>
        this.handleSocialLoginRequest('google'),
      );

    document
      .querySelector('.apple-login-button')
      ?.addEventListener('click', () => this.handleSocialLoginRequest('apple'));

    document
      .querySelector('.show-fields-button')
      ?.addEventListener('click', () => this.handleShowFieldsButtonClick());

    document
      .querySelector('#email')
      ?.addEventListener('change', (e) => this.handleInputTextChange(e));
    document
      .querySelector('#first-name')
      ?.addEventListener('change', (e) => this.handleInputTextChange(e));
    document
      .querySelector('#last-name')
      ?.addEventListener('change', (e) => this.handleInputTextChange(e));
    document
      .querySelector('#password')
      ?.addEventListener('change', (e) => this.handleInputTextChange(e));

    document
      .querySelector('#sign-up-form')
      ?.addEventListener('submit', (e) => this.handleEmailSignUpRequest(e));
    document
      .querySelector('.register-button')
      ?.addEventListener('click', (e) => this.handleEmailSignUpRequest(e));

    document
      .querySelector('.toggle-password')
      ?.addEventListener('click', (e) => this.handleShowTextPasswordButton(e));
    document
      .querySelector('.toggle-password-requirements')
      ?.addEventListener('click', (e) => this.handleMobilePasswordContainer(e));

    document
      .querySelector('.info-tooltip')
      ?.addEventListener('mouseenter', () => this.handlePasswordTooltipEnter());
    document
      .querySelector('.info-tooltip')
      ?.addEventListener('mouseleave', () => this.handlePasswordTooltipLeave());
  },

  /*
  ==================================================
    EVENT HANDLERS
  ==================================================
  */

  handleInputTextChange(e) {
    if (this.getFormState() === 'initial-state') {
      return false;
    }

    const validation = this.validateInputCredentials();

    if (validation.isValid) {
      this.changeFormState('enable-submission');
    } else {
      this.changeFormState('show-fields');
    }
  },

  handleEmailSignUpRequest(e) {
    if (e) {
      e.preventDefault();
    }

    const validation = this.validateInputCredentials();

    if (validation.isValid) {
      this.submitSignUp();
    } else {
      this.showErrorMessage({
        identifier: validation.identifier,
        message: validation.message,
      });
    }
  },

  handleMobilePasswordContainer() {
    document.querySelector('.password-requirements').classList.toggle('active');
  },

  handlePasswordTooltipEnter() {
    const passwordRequirements = document.querySelector(
      '.password-requirements',
    );
    passwordRequirements.classList.add('active');
  },

  handlePasswordTooltipLeave() {
    document.querySelector('.password-requirements').classList.remove('active');
  },

  handleShowFieldsButtonClick() {
    this.changeFormState('show-fields');
  },

  handleShowTextPasswordButton() {
    document
      .querySelector('.toggle-password')
      .classList.toggle('show-password');

    const passwordInput = document.getElementById('password');
    const passwordType =
      passwordInput.getAttribute('type') === 'password' ? 'text' : 'password';

    passwordInput.setAttribute('type', passwordType);
  },

  handleSocialLoginRequest(provider) {
    this.presetLoginSessionData()
      .then(() => this.submitSocialLogInRequest(provider))
      .catch((err) => {
        console.error(err);
        this.showErrorMessage({
          identifier: null,
          message: 'Something went wrong. Please try again.',
        });
      });
  },

  /*
  ==================================================
    LOCAL UTILS
  ==================================================
  */

  clearErrorState() {
    document.querySelector('.error-field').innerHTML = '';
    document.querySelector('.error')?.classList.remove('error');
  },

  changeFormState(newState) {
    const form = document.getElementById('sign-up-form');

    if (form) {
      form.classList.remove(
        'initial-state',
        'show-fields',
        'enable-submission',
      );
      form.classList.add(newState);
    }
  },

  getFormState() {
    const form = document.getElementById('sign-up-form');

    if (form.classList.contains('initial-state')) {
      return 'initial-state';
    } else if (form.classList.contains('show-fields')) {
      return 'show-fields';
    }
    return 'enable-submission';
  },

  getParamsFromContextData() {
    const el = document.getElementById('auth-sign-up-page');
    const raw = el?.dataset.context || '{}';
    return JSON.parse(raw);
  },

  initFor() {
    const params = this.getParamsFromContextData();

    if (params?.source) {
      return `initiated-${params.source}`;
    } else {
      return 'initiated';
    }
  },

  parseDelay(token) {
    if (!token) return null;

    const i = token.lastIndexOf('=');
    if (i < 0 || i >= token.length - 1) return null;

    return parseInt(token.substring(i + 1, token.length));
  },

  presetLoginSessionData() {
    return new Promise((resolve, reject) => {
      const params = this.getParamsFromContextData();
      const { catId, packId, returnTo, source, planId, planName } = params;

      if (!(catId || packId || returnTo || source || planId || planName)) {
        resolve();
        return;
      }

      const opts = { body: JSON.stringify(params) };

      postJson('/api/sessions/pre', opts)
        .then(() => resolve('ok'))
        .catch((err) => {
          this.showErrorMessage(err);
          reject(err);
        });
    });
  },

  ratePassword(password) {
    // when all conditions are met, quality will add up to 10

    let quality = Math.min(7, password.length);
    let message;

    if (password.length < 8) {
      quality = Math.max(0, quality - 1);
      message = 'Password requires at least 8 characters.';
    }

    if (password.length > 128) {
      quality = Math.max(0, quality - 1);
      message = message || 'Password can not be more than 128 characters.';
    }

    if (password !== password.toUpperCase()) {
      quality = quality + 1;
    } else {
      message = message || 'Password does not contain a lower case letter.';
    }

    if (password !== password.toLowerCase()) {
      quality = quality + 1;
    } else {
      message = message || 'Password does not contain an upper case letter.';
    }

    if (password.match(/\d+/g)) {
      quality = quality + 1;
    } else {
      message = message || 'Password does not contain a number.';
    }

    if (password.search(/[^a-zA-Z0-9!#%+:=?@. ]/) !== -1) {
      quality = Math.max(0, quality - 1);
      message =
        'Password contains an unsupported character. Supported special characters include !#%+:=?@. and [space].';
    }

    return { passwordQuality: quality, ratingMessage: message };
  },

  showErrorMessage(err) {
    this.clearErrorState();

    if (!err || !err.message) {
      return null;
    }

    if (err) {
      if (err.identifier) {
        document.getElementById(err.identifier).classList.add('error');
      }

      const errorField = document.querySelector('.error-field');
      const errorMsg = document.createElement('span');
      errorMsg.classList.add('error-text');
      errorMsg.textContent = err.message || err;
      errorField.appendChild(errorMsg);
    }
  },

  submitSignUp() {
    const button = document.querySelector('.register-button');
    button.classList.add('loading');

    const emailInput = document.getElementById('email').value.trim();
    const firstNameInput = document.getElementById('first-name').value.trim();
    const lastNameInput = document.getElementById('last-name').value.trim();
    const passwordInput = document.getElementById('password').value.trim();

    const params = this.getParamsFromContextData();
    params.email = emailInput;
    params.firstName = firstNameInput;
    params.lastName = lastNameInput;
    params.password = passwordInput;
    params.rememberMe = true;
    params.terms = true;
    params.web = true;

    CookieHelper.setCookie('x_tzo', new Date().getTimezoneOffset());

    const opts = { body: JSON.stringify(params) };
    postJson('/api/sessions/sign_up', opts)
      .then((rs) => (window.location = rs.redirect_to || '/'))
      .catch((err) => {
        button.classList.remove('loading');
        this.showErrorMessage(err);
      });
  },

  submitSocialLogInRequest(provider) {
    if (provider === 'apple') {
      AppleID.auth.signIn();
    } else if (provider === 'google') {
      const f = document.getElementById('google-form');
      if (f) {
        f.submit();
      } else {
        console.error('Could not find the login provider');
      }
    }
  },

  trackSignupInitiated() {
    const name = this.initFor();
    EventBus.publish('web:reg:dialog', { name: name }, true);

    if (!window.gtag) return;

    const params = this.getParamsFromContextData();
    const source = params?.source || null;

    gtag('event', 'page_view', {
      page_location: document.referrer || null,
      send_to: GaHelper.ga_4_id(),
    });

    gtag('event', 'registration_started', {
      catId: params?.catId || null,
      packId: params?.packId || null,
      plan: params?.planName || null,
      returnTo: params?.returnTo || null,
      send_to: GaHelper.ga_4_id(),
      source: source,
    });
  },

  validateInputCredentials() {
    const emailInput = document.getElementById('email')?.value.trim();
    const firstNameInput = document.getElementById('first-name')?.value.trim();
    const lastNameInput = document.getElementById('last-name')?.value.trim();
    const passwordInput = document.getElementById('password')?.value.trim();

    if (!(emailInput && passwordInput)) {
      return {
        isValid: false,
        identifier: null,
        message: 'Please enter an e-mail, first and last name, and password.',
      };
    }

    if (emailInput) {
      if (!StringHelper.isValidEmail(emailInput)) {
        return {
          isValid: false,
          identifier: 'email',
          message: 'Please enter a valid email address.',
        };
      }

      if (!firstNameInput) {
        return {
          isValid: false,
          identifier: 'first-name',
          message: 'Please enter both a first and last name.',
        };
      }

      if (!lastNameInput) {
        return {
          isValid: false,
          identifier: 'last-name',
          message: 'Please enter both a first and last name.',
        };
      }

      if (passwordInput) {
        const { passwordQuality, ratingMessage } =
          this.ratePassword(passwordInput);

        if (passwordQuality < 10) {
          return {
            isValid: false,
            identifier: 'password',
            message: ratingMessage,
          };
        }
      }
    }

    return {
      isValid: true,
      identifier: null,
      message: null,
    };
  },
};
